import { supabase } from "~/integrations/supabase/client";

import type {
  AdjustmentFilters,
  AdjustmentReasonCode,
  AdjustmentStats,
  BulkAdjustmentItem,
  BulkAdjustmentResult,
  StockAdjustment,
  StockAdjustmentFormData,
  StockAdjustmentWithRelations,
} from "~/types/stockAdjustment";

export const adjustmentService = {
  // ========== Reason Codes ==========

  /**
   * Get all active reason codes, optionally filtered by adjustment type
   */
  async getReasonCodes(
    adjustmentType?: "increment" | "decrement"
  ): Promise<AdjustmentReasonCode[]> {
    let query = supabase
      .from("wms_adjustment_reason_codes")
      .select("*")
      .eq("is_active", true)
      .order("display_order", { ascending: true });

    if (adjustmentType) {
      query = query.or(
        `adjustment_type.eq.${adjustmentType},adjustment_type.eq.both`
      );
    }

    const { data, error } = await query;
    if (error) throw error;
    return (data || []) as AdjustmentReasonCode[];
  },

  /**
   * Get a single reason code by ID
   */
  async getReasonCodeById(id: string): Promise<AdjustmentReasonCode | null> {
    const { data, error } = await supabase
      .from("wms_adjustment_reason_codes")
      .select("*")
      .eq("id", id)
      .single();
    if (error) return null;
    return data as AdjustmentReasonCode;
  },

  // ========== Stock Adjustments ==========

  /**
   * Get adjustments with optional filters
   */
  async getAdjustments(
    filters?: AdjustmentFilters,
    limit = 100
  ): Promise<StockAdjustmentWithRelations[]> {
    let query = supabase
      .from("wms_stock_adjustments")
      .select(
        `
        *,
        reason_code:wms_adjustment_reason_codes(*),
        product:products(id, short_name, stock),
        raw_material:raw_materials(id, name, current_stock),
        oil_type:oil_types(id, name, current_stock_liters),
        position:wms_positions(id, full_code),
        pallet:wms_pallets(id, code),
        performer:profiles!wms_stock_adjustments_performed_by_fkey(id, name, lastname)
      `
      )
      .order("performed_at", { ascending: false });

    if (filters?.adjustment_type) {
      query = query.eq("adjustment_type", filters.adjustment_type);
    }
    if (filters?.reason_code_id) {
      query = query.eq("reason_code_id", filters.reason_code_id);
    }
    if (filters?.product_id) {
      query = query.eq("product_id", filters.product_id);
    }
    if (filters?.position_id) {
      query = query.eq("position_id", filters.position_id);
    }
    if (filters?.from_date) {
      query = query.gte("performed_at", filters.from_date);
    }
    if (filters?.to_date) {
      query = query.lte("performed_at", filters.to_date);
    }
    if (filters?.status) {
      query = query.eq("status", filters.status);
    }

    const { data, error } = await query.limit(limit);
    if (error) throw error;
    return (data || []) as StockAdjustmentWithRelations[];
  },

  /**
   * Get a single adjustment by ID with all relations
   */
  async getAdjustmentById(
    id: string
  ): Promise<StockAdjustmentWithRelations | null> {
    const { data, error } = await supabase
      .from("wms_stock_adjustments")
      .select(
        `
        *,
        reason_code:wms_adjustment_reason_codes(*),
        product:products(id, short_name, stock),
        raw_material:raw_materials(id, name, current_stock),
        oil_type:oil_types(id, name, current_stock_liters),
        position:wms_positions(id, full_code),
        pallet:wms_pallets(id, code),
        performer:profiles!wms_stock_adjustments_performed_by_fkey(id, name, lastname)
      `
      )
      .eq("id", id)
      .single();
    if (error) throw error;
    return data as StockAdjustmentWithRelations;
  },

  /**
   * Get adjustment history for a specific product
   */
  async getAdjustmentsByProduct(
    productId: string
  ): Promise<StockAdjustmentWithRelations[]> {
    const { data, error } = await supabase
      .from("wms_stock_adjustments")
      .select(
        `
        *,
        reason_code:wms_adjustment_reason_codes(*),
        product:products(id, short_name, stock),
        position:wms_positions(id, full_code),
        performer:profiles!wms_stock_adjustments_performed_by_fkey(id, name, lastname)
      `
      )
      .eq("product_id", productId)
      .order("performed_at", { ascending: false });
    if (error) throw error;
    return (data || []) as StockAdjustmentWithRelations[];
  },

  /**
   * Create a new stock adjustment
   * This handles all the logic that would otherwise be in a trigger:
   * 1. Insert the adjustment record
   * 2. Insert stock_movements record (for products) or ingredient_stock_movements (for raw materials)
   * 3. Insert wms_movements record (if position specified, products only)
   * 4. Update products.stock or raw_materials.current_stock
   * 5. Update wms_inventory (if position specified, products only)
   */
  async createAdjustment(
    data: StockAdjustmentFormData,
    performedBy: string
  ): Promise<StockAdjustmentWithRelations> {
    // Get the reason code to check requirements
    const reasonCode = await this.getReasonCodeById(data.reason_code_id);
    if (!reasonCode) {
      throw new Error("Invalid reason code");
    }

    const itemType = data.item_type || "product";
    const isIngredient = itemType === "raw_material" || itemType === "oil";

    // Validate increment requirements (only for products)
    if (data.adjustment_type === "increment" && !isIngredient) {
      if (reasonCode.requires_lot_number && !data.lot_number) {
        throw new Error("Lot number is required for this adjustment type");
      }
      if (reasonCode.requires_expiration_date && !data.expiration_date) {
        throw new Error("Expiration date is required for this adjustment type");
      }
    }

    const now = new Date().toISOString();
    const quantityChange =
      data.adjustment_type === "increment" ? data.quantity : -data.quantity;

    // Warehouse-level product adjustments (B1/B2/NC) go through the atomic RPC:
    // it creates the adjustment row, adjusts the bodega default bucket, logs a
    // wms_movements leg and syncs products.stock (the AFTER UPDATE trigger logs the
    // stock_movements row). No position needed. The old position-based path below
    // is only a fallback when no warehouse code is supplied.
    if (itemType === "product" && data.warehouse_code) {
      const { data: rpcResult, error: rpcError } = await supabase.rpc(
        "create_product_stock_adjustment" as never,
        {
          p_warehouse_code: data.warehouse_code,
          p_product_id: data.product_id,
          p_adjustment_type: data.adjustment_type,
          p_quantity: data.quantity,
          p_reason_code_id: data.reason_code_id,
          p_lot_number: data.lot_number || null,
          p_production_batch_id: data.production_batch_id ?? null,
          p_expiration_date: data.expiration_date || null,
          p_notes: data.notes || null,
          p_discrepancy_cause: data.discrepancy_cause || null,
          p_performed_by: performedBy,
          // Re-weights products.unit_cost_clp on an ingreso (increment); ignored
          // by the RPC on decrements.
          p_unit_cost: data.unit_cost ?? null,
        } as never
      );
      if (rpcError) throw rpcError;
      const adjustmentId = (rpcResult as unknown as { adjustment_id: string })
        .adjustment_id;
      return this.getAdjustmentById(
        adjustmentId
      ) as Promise<StockAdjustmentWithRelations>;
    }

    // Step 1: Create the adjustment record
    const adjustmentData = {
      adjustment_type: data.adjustment_type,
      reason_code_id: data.reason_code_id,
      item_type: itemType,
      product_id: itemType === "product" ? data.product_id : null,
      raw_material_id:
        itemType === "raw_material" ? data.raw_material_id : null,
      oil_type_id: itemType === "oil" ? data.oil_type_id : null,
      plant: isIngredient ? (data.plant ?? null) : null,
      unit: data.unit || null,
      // Cost of an ingredient ingreso (feeds the weighted-average cost); only
      // meaningful for ingredient increments.
      unit_cost:
        isIngredient && data.adjustment_type === "increment"
          ? (data.unit_cost ?? null)
          : null,
      position_id: itemType === "product" ? data.position_id || null : null,
      pallet_id: itemType === "product" ? data.pallet_id || null : null,
      inventory_id: itemType === "product" ? data.inventory_id || null : null,
      quantity: data.quantity,
      lot_number: data.lot_number || null,
      expiration_date: data.expiration_date || null,
      requires_destruction_certificate:
        reasonCode.requires_destruction_certificate,
      destruction_certificate_number:
        data.destruction_certificate_number || null,
      destruction_certificate_date: data.destruction_certificate_date || null,
      destruction_certificate_notes: data.destruction_certificate_notes || null,
      discrepancy_cause: data.discrepancy_cause || null,
      notes: data.notes || null,
      status: "completed" as const,
      performed_by: performedBy,
      performed_at: now,
    };

    const { data: adjustment, error: adjustmentError } = await supabase
      .from("wms_stock_adjustments")
      .insert(adjustmentData)
      .select()
      .single();

    if (adjustmentError) throw adjustmentError;

    const reasonText = `${reasonCode.code}: ${reasonCode.name}`;

    let stockMovementId: string | null = null;

    if (isIngredient) {
      // Route ingredient (raw_material / oil) adjustments through the sync RPC.
      // It updates the per-plant table (source of truth) AND the cache total AND
      // logs the ingredient_stock_movements row, all scoped to the chosen plant.
      // Never UPDATE the cache columns directly (see CLAUDE.md "Stock data model").
      const { error: rpcError } = await supabase.rpc(
        "create_stock_adjustment",
        {
          p_ingredient_type: itemType,
          p_oil_type_id: itemType === "oil" ? (data.oil_type_id ?? null) : null,
          p_raw_material_id:
            itemType === "raw_material" ? (data.raw_material_id ?? null) : null,
          p_quantity: quantityChange, // signed: decrement => negative
          p_unit: data.unit ?? null,
          p_notes: reasonText,
          p_plant: data.plant ?? null,
          // Re-weights average_cost_clp on an ingreso (increment); ignored on
          // decrements. NULL keeps the WAC unchanged (legacy behavior).
          p_unit_cost:
            data.adjustment_type === "increment"
              ? (data.unit_cost ?? null)
              : null,
        }
      );

      if (rpcError) throw rpcError;
    } else {
      // Step 2b: Create stock_movements record for products
      const { data: stockMovement, error: stockMovementError } = await supabase
        .from("stock_movements")
        .insert({
          product_id: data.product_id,
          movement_type: "adjustment",
          quantity_change: quantityChange,
          reference_type: "manual_adjustment",
          reference_id: adjustment.id,
          reason: reasonText,
          created_by: performedBy,
        })
        .select()
        .single();

      if (stockMovementError) throw stockMovementError;
      stockMovementId = stockMovement.id;

      // Step 3: Create wms_movements record (if position specified, products only)
      let wmsMovementId: string | null = null;
      if (data.position_id) {
        const { data: wmsMovement, error: wmsMovementError } = await supabase
          .from("wms_movements")
          .insert({
            movement_type: "adjustment",
            pallet_id: data.pallet_id || null,
            product_id: data.product_id,
            lot_number: data.lot_number || null,
            box_quantity: data.quantity,
            from_position_id:
              data.adjustment_type === "decrement" ? data.position_id : null,
            to_position_id:
              data.adjustment_type === "increment" ? data.position_id : null,
            reference_type: "manual",
            reference_id: adjustment.id,
            reason: reasonText,
            notes: data.notes || null,
            performed_by: performedBy,
            performed_at: now,
          })
          .select()
          .single();

        if (wmsMovementError) throw wmsMovementError;
        wmsMovementId = wmsMovement.id;
      }

      // Step 4b: Update products.stock
      const { error: productUpdateError } = await supabase.rpc(
        "increment_product_stock",
        {
          p_product_id: data.product_id,
          p_quantity: quantityChange,
        }
      );

      // If RPC doesn't exist, fall back to direct update
      if (productUpdateError) {
        const { data: currentProduct, error: fetchError } = await supabase
          .from("products")
          .select("stock")
          .eq("id", data.product_id)
          .single();

        if (fetchError) throw fetchError;

        const newStock = (currentProduct.stock || 0) + quantityChange;

        const { error: updateError } = await supabase
          .from("products")
          .update({ stock: newStock, updated_at: now })
          .eq("id", data.product_id);

        if (updateError) throw updateError;
      }

      // Step 5: Update wms_inventory (if position specified)
      if (data.position_id) {
        if (data.adjustment_type === "decrement" && data.inventory_id) {
          // Decrement: reduce box_count or delete if empty
          const { data: currentInventory, error: fetchInvError } =
            await supabase
              .from("wms_inventory")
              .select("box_count")
              .eq("id", data.inventory_id)
              .single();

          if (fetchInvError) throw fetchInvError;

          const newBoxCount = (currentInventory.box_count || 0) - data.quantity;

          if (newBoxCount <= 0) {
            // Delete the inventory record
            const { error: deleteError } = await supabase
              .from("wms_inventory")
              .delete()
              .eq("id", data.inventory_id);
            if (deleteError) throw deleteError;
          } else {
            // Update the count
            const { error: updateInvError } = await supabase
              .from("wms_inventory")
              .update({ box_count: newBoxCount, updated_at: now })
              .eq("id", data.inventory_id);
            if (updateInvError) throw updateInvError;
          }
        } else if (data.adjustment_type === "increment") {
          // Increment: add to existing or create new inventory record
          // Try to find existing inventory at this position with same lot
          const { data: existingInventory, error: findError } = await supabase
            .from("wms_inventory")
            .select("id, box_count")
            .eq("position_id", data.position_id)
            .eq("product_id", data.product_id)
            .eq("lot_number", data.lot_number || "")
            .maybeSingle();

          if (findError) throw findError;

          if (existingInventory) {
            // Update existing
            const { error: updateInvError } = await supabase
              .from("wms_inventory")
              .update({
                box_count: existingInventory.box_count + data.quantity,
                updated_at: now,
              })
              .eq("id", existingInventory.id);
            if (updateInvError) throw updateInvError;
          } else {
            // Create new inventory record
            const { error: insertInvError } = await supabase
              .from("wms_inventory")
              .insert({
                position_id: data.position_id,
                pallet_id: data.pallet_id || null,
                product_id: data.product_id,
                lot_number: data.lot_number || null,
                box_count: data.quantity,
                reserved_boxes: 0,
                expiration_date: data.expiration_date || null,
                received_at: now,
              });
            if (insertInvError) throw insertInvError;
          }
        }
      }

      // Step 6: Update the adjustment with linked movement IDs
      const { error: linkError } = await supabase
        .from("wms_stock_adjustments")
        .update({
          stock_movement_id: stockMovement.id,
          wms_movement_id: wmsMovementId,
        })
        .eq("id", adjustment.id);

      if (linkError) throw linkError;
    } // End of product-specific logic

    // Return the full adjustment with relations
    return this.getAdjustmentById(
      adjustment.id
    ) as Promise<StockAdjustmentWithRelations>;
  },

  /**
   * Apply many adjustments in one go (Ajuste Masivo). Each row is resolved and
   * validated in the dialog, then applied here by reusing the same per-row path as
   * a single adjustment (createAdjustment dispatches product vs ingredient RPCs).
   * Rows are applied sequentially and independently: one failing row is captured in
   * its result and does not roll back the rest, so the caller gets a per-row report.
   */
  async createAdjustmentsBulk(
    items: BulkAdjustmentItem[],
    performedBy: string
  ): Promise<BulkAdjustmentResult[]> {
    const results: BulkAdjustmentResult[] = [];
    for (const item of items) {
      try {
        await this.createAdjustment(item.data, performedBy);
        results.push({
          index: item.index,
          tipo: item.tipo,
          codigo: item.codigo,
          itemName: item.itemName,
          ok: true,
        });
      } catch (err) {
        results.push({
          index: item.index,
          tipo: item.tipo,
          codigo: item.codigo,
          itemName: item.itemName,
          ok: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }
    return results;
  },

  // ========== Analytics ==========

  /**
   * Get adjustment statistics for a given time period
   */
  async getAdjustmentStats(days = 30): Promise<AdjustmentStats> {
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - days);

    const { data, error } = await supabase
      .from("wms_stock_adjustments")
      .select(
        `
        adjustment_type,
        quantity,
        reason_code:wms_adjustment_reason_codes(code, name)
      `
      )
      .gte("performed_at", fromDate.toISOString())
      .eq("status", "completed");

    if (error) throw error;

    const stats: AdjustmentStats = {
      total_increments: 0,
      total_decrements: 0,
      increment_quantity: 0,
      decrement_quantity: 0,
      by_reason: [],
    };

    const byReason = new Map<
      string,
      { code: string; name: string; count: number; quantity: number }
    >();

    for (const adj of data || []) {
      if (adj.adjustment_type === "increment") {
        stats.total_increments++;
        stats.increment_quantity += adj.quantity;
      } else {
        stats.total_decrements++;
        stats.decrement_quantity += adj.quantity;
      }

      const code = adj.reason_code?.code || "Unknown";
      const name = adj.reason_code?.name || "Unknown";
      const existing = byReason.get(code) || {
        code,
        name,
        count: 0,
        quantity: 0,
      };
      existing.count++;
      existing.quantity += adj.quantity;
      byReason.set(code, existing);
    }

    stats.by_reason = Array.from(byReason.values()).sort(
      (a, b) => b.count - a.count
    );

    return stats;
  },
};
